import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'

/**
 * Right-hand edit surface. Every change in the console happens in here, so a
 * misclick never writes anything — you open, edit, and explicitly save.
 * Full-width below the sidebar breakpoint.
 */
export default function Drawer({ open, kicker, title, onClose, children }) {
  const panelRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    // Move focus in so Escape and tabbing act on the drawer, not the page behind.
    const first = panelRef.current?.querySelector('input, select, textarea, button')
    first?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[60] bg-[#060c0b]/40 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Edit'}
        className={`fixed inset-y-0 right-0 z-[70] w-full overflow-y-auto border-l border-gray-200 bg-white shadow-2xl transition-transform duration-200 desk:w-[400px] ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {open && (
          <div className="px-5 pb-8 pt-6">
            {kicker && (
              <p className="text-[11px] font-bold uppercase tracking-[.09em] text-[#e2932b]">{kicker}</p>
            )}
            <div className="mb-5 mt-1.5 flex items-start justify-between gap-3">
              <h2 className="text-[1.3rem] font-bold tracking-[-.01em] text-gray-900">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 transition hover:bg-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {children}
          </div>
        )}
      </aside>
    </>
  )
}

export function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="mb-1.5 block text-[11.5px] font-semibold text-gray-600">{label}</label>
      {children}
    </div>
  )
}

export const fieldClass =
  'w-full rounded-[9px] border border-gray-200 bg-gray-50 px-3 py-2.5 text-[13.5px] text-gray-900 outline-none focus:border-transparent focus:ring-2 focus:ring-[#e2932b]'

export function Hint({ children }) {
  return (
    <p className="mb-4 rounded-[10px] border border-gray-200 bg-gray-50 px-3 py-2.5 text-[12px] text-gray-500">
      {children}
    </p>
  )
}

export function Actions({ onCancel, onSave, saveLabel = 'Save changes', busy }) {
  return (
    <div className="mt-6 flex gap-2.5">
      <button
        type="button"
        onClick={onSave}
        disabled={busy}
        className="flex-1 rounded-[9px] bg-[#c97a1a] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#b06914] disabled:opacity-60"
      >
        {busy ? 'Saving…' : saveLabel}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-[9px] border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
      >
        Cancel
      </button>
    </div>
  )
}

export function DangerZone({ label, note, onConfirm, busy }) {
  return (
    <div className="mt-7 border-t border-gray-200 pt-4">
      {note && <p className="mb-2.5 text-[12px] text-gray-500">{note}</p>}
      <button
        type="button"
        onClick={onConfirm}
        disabled={busy}
        className="w-full rounded-[9px] border border-[#d33d3d]/30 px-4 py-2.5 text-sm font-semibold text-[#d33d3d] transition hover:bg-[#d33d3d]/10 disabled:opacity-60"
      >
        {label}
      </button>
    </div>
  )
}
